import React, { useState, useContext, useEffect, useRef } from 'react';
import { Form, useFormState } from 'react-final-form';
import type { FormRenderProps } from 'react-final-form';
import { withI18n, withI18nProps } from '@lingui/react';
import { Heading2 } from '@qover/typography';
// @ts-ignore
import { ThemeContext } from '@qover/styles';
import {
  AnimatedHorizontalScroll,
  BackExternalLinkSection,
  PrimaryButton,
  SelectField,
  InputCurrencyField,
} from '@qover/molecules';
import { LabelType } from '@qover/domain';
import { useFeature, useFieldValue, useForm } from '@qover/hooks';
import { DiscountCode } from '@qover-private/protos-web/qover/dojo/contracts/v1/contract';
import { Footnote } from '@qover/typography';
import BikeAccordionFaq from '../../components/BikeAccordionFaq';
import { content1 } from '../../components/bikeAccordionFaq.messages';
import messages from './bikeQuote.messages';
import { Container } from '../../bike.styles';
import {
  CardsContainer,
  FieldsContainer,
  PageContainer,
} from './bikeQuote.styles';
import {
  pushProductDetailsView,
  createMainProduct,
  pushAddPromocode,
} from '../../domain/customDataLayer';
import { Draft, DraftProps } from '../../domain/Draft';
import { Type } from '../../domain/Risk';
import { Variant } from '../../domain/Terms';
import { PriceInfo } from '../../domain/PriceInfo';
import { BikeDiscountBar } from '../../components/BikeDiscountBar';
import { BikeVariantCard } from '../../components/BikeVariantCard';
import { messagesPerVariant, messagesPerBikeType } from '../../bike.messages';
import { BikeLabels } from '../../components/BikeLabels';
import { quoteValidation } from '../../utils/bikeValidation';
import { LocaleContext } from '@qover/contexts';
import { Wizard } from 'packages/hooks/src/useDraft';

type BikeQuoteProps = withI18nProps & {
  draft: Draft;
  onSubmit: FormRenderProps['handleSubmit'];
  prices: PriceInfo;
  step: string;
  variants: Variant[];
  wizard: Wizard<Draft>;
};

const BikeQuote: React.FC<BikeQuoteProps> = props => {
  const containerCardsRef = useRef<HTMLDivElement>(null);
  const [dirty, setDirty] = useState(false);
  const discountCode = useFieldValue<DiscountCode>('discountCodes[0]');
  const labelType = useFeature<LabelType>('labelType') ?? 'floating';
  const formState = useFormState<DraftProps>();
  const { change, reset, getState } = useForm();
  const theme = useContext<any>(ThemeContext);
  const { locale } = useContext(LocaleContext);

  const landingPageUrl =
    theme?.featureToggles?.landingPageUrl?.[locale.value] ?? '';

  useEffect(() => {
    const state = getState();

    if (state.dirtySinceLastSubmit) {
      setTimeout(reset);
    }

    if (props.prices.initialized && props.variants.length) {
      pushProductDetailsView(
        formState.values,
        props.prices,
        props.variants,
        discountCode.name,
      );
    }
  }, [props.prices, props.variants]);

  const addDiscount = async (discount: string) => {
    await props.wizard.addDiscount(new Draft(formState.values), discount);
    pushAddPromocode(discount, props.step);
  };

  const removeDiscount = async (discount: string) => {
    await props.wizard.removeDiscount(new Draft(formState.values), discount);
  };

  const typeOptions = [
    Type.TYPE_REGULAR_BIKE,
    Type.TYPE_REGULAR_EBIKE,
    Type.TYPE_RACE_BIKE,
    Type.TYPE_RACE_EBIKE,
    Type.TYPE_MOUNTAIN_BIKE,
    Type.TYPE_MOUNTAIN_EBIKE,
    Type.TYPE_CARGO_BIKE,
    Type.TYPE_CARGO_EBIKE,
  ].map((type: Type) => ({
    // @ts-ignore
    label: props.i18n._(messagesPerBikeType[type].optionSelect),
    value: type,
  }));

  const handleGetPriceInfo = () => {
    setDirty(false);
    props.draft.updateRisk(formState.values.risk);
    props.draft.updateTerms(formState.values.terms);

    return props.wizard.getPriceInfo().then(() => {
      if (containerCardsRef.current) {
        containerCardsRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    });
  };

  const handleVariantChange = (variant: Variant) => () => {
    createMainProduct(
      formState.values,
      props.prices,
      variant,
      props.draft.draftId ? 'update' : 'landing',
    );
    change('terms.variant', variant);
    return props.onSubmit();
  };

  const variants = props.variants.length
    ? props.variants
    : [Variant.VARIANT_UNSPECIFIED, Variant.VARIANT_UNSPECIFIED];

  return (
    <>
      <PageContainer>
        <Heading2 className="px-40">{messages.title}</Heading2>
        <Container>
          <Form
            initialValues={formState.values}
            onSubmit={handleGetPriceInfo}
            validate={quoteValidation}
          >
            {({ handleSubmit }) => (
              <form onSubmit={handleSubmit} method="post" name="priceInfoForm">
                <FieldsContainer className="px-40">
                  <SelectField
                    data-test="bike.quote.type"
                    id="bike.quote.type"
                    label={messages.type}
                    name="risk.type"
                    emptyChoice
                    onChange={e => {
                      setDirty(true);
                      change('risk.type', e.target.value);
                    }}
                    options={typeOptions}
                  />
                  <InputCurrencyField
                    data-test="bike.quote.originalValue"
                    id="bike.quote.originalValue"
                    label={messages.originalValue}
                    name="risk.originalValue"
                    onChange={e => {
                      setDirty(true);
                      change('risk.originalValue', e.target.value * 100);
                    }}
                  />
                  <div
                    className={labelType === 'static' ? 'static' : undefined}
                  >
                    <PrimaryButton data-test="bike.quote.priceInfoButton">
                      {messages.getPrice}
                    </PrimaryButton>
                  </div>
                </FieldsContainer>
              </form>
            )}
          </Form>
          <CardsContainer ref={containerCardsRef}>
            <AnimatedHorizontalScroll>
              {variants.map((variant, idx) => {
                const BikeLabelsComponent = BikeLabels[variant];

                return (
                  <BikeVariantCard
                    banner={
                      idx === variants.length - 1 ? messages.banner : undefined
                    }
                    data-test={`bike.quote.card.${variant}`}
                    discount={props.prices.getDiscount(variant)}
                    dirty={dirty}
                    key={`${variant}-${idx}`}
                    discountCode={discountCode}
                    onClick={handleVariantChange(variant)}
                    price={props.prices.getBasePrice(variant)}
                    discountPrice={props.prices.getPrice(variant)}
                    title={messagesPerVariant[variant].optionTitle}
                  >
                    <BikeLabelsComponent />
                  </BikeVariantCard>
                );
              })}
            </AnimatedHorizontalScroll>
          </CardsContainer>
          {discountCode && !discountCode.isRecurring && (
            <Footnote className="px-40 text-center">
              {messages.discountCaption}
            </Footnote>
          )}
        </Container>
      </PageContainer>
      {theme?.featureToggles?.showDiscount && (
        <BikeDiscountBar
          addDiscount={addDiscount}
          removeDiscount={removeDiscount}
        />
      )}
      <BackExternalLinkSection href={landingPageUrl} />
      <BikeAccordionFaq translations={content1} />
    </>
  );
};

export default withI18n()(BikeQuote);
